import {
    type CostReport,
    GetCostReportsParams,
    GetRevenueEntriesParams,
    GetTimeReportsParams,
    type Permission,
    type RevenueEntry,
    RevenueType,
    TimeEntryType,
    type TimeReport,
    dateAdd,
    formatDateShort,
    formatNumber,
    formatWeekDayShort,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import type { ChartDefinition } from "@pentacode/ui/charts";
import { chartSeriesColors, lineChart } from "@pentacode/ui/charts";
import "@pentacode/ui/components";
import type { ChartPointEventDetail } from "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { alert } from "../../elements/alert-dialog";
import "../../elements/scroller";
import { app } from "../../init";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";

interface Chart {
    title: string;
    getData: (date: string) => number;
    unit: string;
    inverted?: boolean;
    total: "sum" | "average";
    data: {
        curr: number[];
        prev: number[];
    };
    scopes: Permission[];
    definition?: ChartDefinition;
}

@customElement("ptc-reports-overview")
export class ReportsOverview extends Routing(StateMixin(LitElement)) {
    routePattern = /^reports\/overview/;

    get routeTitle() {
        return this.venue ? `Berichte Übersicht: ${this.venue.name}` : undefined;
    }

    @state()
    private _loading = false;

    @state()
    private _timeReports: TimeReport[] = [];

    @state()
    private _costReports: CostReport[] = [];

    @state()
    private _salesEntries: RevenueEntry[];

    @state()
    private _range = 12;

    @state()
    private _resolution = 7;

    @state()
    private _showDataPoint: number | null = null;

    @state()
    private _charts: Chart[] = [
        {
            title: "Einnahmen",
            unit: "€",
            getData: (d: string) => this._getSalesData(d),
            total: "sum",
            scopes: ["manage.reports.revenue"],
            data: {
                curr: [],
                prev: [],
            },
        },
        {
            title: "Personalkosten",
            unit: "€",
            inverted: true,
            getData: (d: string) => this._getCostsData(d),
            total: "sum",
            scopes: ["manage.reports.costs"],
            data: {
                curr: [],
                prev: [],
            },
        },
        {
            title: "Arbeitszeit",
            unit: "Std.",
            inverted: true,
            getData: (d: string) => this._getHoursData(d),
            total: "sum",
            scopes: ["manage.reports.time"],
            data: {
                curr: [],
                prev: [],
            },
        },
        {
            title: "Produktivität",
            unit: "€/St",
            getData: (d: string) => {
                const sales = this._getSalesData(d);
                const hours = this._getHoursData(d);
                return hours ? sales / hours : 0;
            },
            total: "average",
            scopes: ["manage.reports.time", "manage.reports.revenue"],
            data: {
                curr: [],
                prev: [],
            },
        },
    ];

    @query(".venue-selector")
    private _venueSelector: HTMLSelectElement;

    private get _today() {
        return (this.router.params.today as DateString) || toDateString(new Date());
    }

    private get _currRange() {
        const today = this._today;

        const dates: string[] = [];
        const bulkSize = this._range * this._resolution;

        for (let days = -1 * bulkSize; days < 0; days++) {
            dates.push(dateAdd(today, { days }));
        }

        return dates;
    }

    private get _prevRange() {
        const today = this._today;

        const dates: string[] = [];
        const bulkSize = this._range * this._resolution;

        for (let days = -2 * bulkSize; days < -bulkSize; days++) {
            dates.push(dateAdd(today, { days }));
        }

        return dates;
    }

    private get _enabledCharts() {
        return this._charts.filter((c) => c.scopes.every((p) => app.hasPermission(p)));
    }

    async handleRoute() {
        if (!this.venue || !app.hasAccess({ venue: this.venue })) {
            if (app.accessibleVenues.length) {
                this.go(null, { ...this.router.params, venue: app.accessibleVenues[0].id.toString() }, true);
            }
            return;
        }

        void this.load();
    }

    async load(range = 7, resolution = 1) {
        if (!this.venue) {
            return;
        }

        this._range = range;
        this._resolution = resolution;

        const to = this._today;
        const from = dateAdd(to, { days: -2 * this._range * this._resolution });

        this._loading = true;

        try {
            const [salesEntries, timeReports, costReports] = await Promise.all([
                this._loadRevenueEntries(from, to, this.venueId),
                this._loadTimeReports(from, to, this.venueId),
                this._loadCostReports(from, to, this.venueId),
            ]);

            this._salesEntries = salesEntries;
            this._timeReports = timeReports;
            this._costReports = costReports;
            this._updateCharts();
        } catch (e) {
            const message = e instanceof Error ? e.message : "Unbekannter Fehler";
            void alert(message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _loadRevenueEntries(from: DateString, to: DateString, venueId: number) {
        if (app.hasPermission("manage.reports.revenue")) {
            return app.api.getRevenueEntries(
                new GetRevenueEntriesParams({
                    from,
                    to,
                    venue: venueId,
                    type: [RevenueType.Sales],
                    reporting: true,
                })
            );
        }

        return [];
    }

    private async _loadTimeReports(from: DateString, to: DateString, venueId: number) {
        if (app.hasPermission("manage.reports.time")) {
            return app.api.getTimeReports(
                new GetTimeReportsParams({
                    from,
                    to,
                    filters: [{ type: "venue", value: venueId }],
                    resolution: "day",
                    types: [TimeEntryType.Work],
                })
            );
        }

        return [];
    }

    private async _loadCostReports(from: DateString, to: DateString, venueId: number) {
        if (app.hasPermission("manage.reports.costs")) {
            return app.api.getCostReports(
                new GetCostReportsParams({
                    from,
                    to,
                    filters: [{ type: "venue", value: venueId }],
                    resolution: "day",
                })
            );
        }

        return [];
    }

    private _selectVenue() {
        this.go(null, { venue: this._venueSelector.value });
    }

    private _getSalesData(date: string) {
        return this._salesEntries.filter((e) => e.date === date).reduce((total, entry) => total + entry.amount, 0);
    }

    private _getHoursData(date: string) {
        return this._timeReports
            .filter((e) => e.from === date)
            .flatMap((e) => e.items)
            .reduce((total, entry) => total + entry.hours, 0);
    }

    private _getCostsData(date: string) {
        return this._costReports
            .filter((e) => e.from === date)
            .flatMap((e) => e.items)
            .reduce(
                (total, entry) =>
                    total +
                    entry.base.totalCosts +
                    entry.breaks.totalCosts +
                    entry.bonuses.totalCosts +
                    entry.commission.totalCosts +
                    entry.meals.totalCosts,
                0
            );
    }

    private _updateCharts() {
        for (const chart of this._enabledCharts) {
            chart.data = this._getChartData(chart);
            chart.definition = this._getChartDefinition(chart);
        }

        this.requestUpdate();
    }

    private _getChartData(chart: Chart) {
        const curr: number[] = [];
        const prev: number[] = [];

        for (let i = 0; i < this._range; i++) {
            const currVal = this._currRange
                .slice(i * this._resolution, (i + 1) * this._resolution)
                .reduce((total, d) => total + chart.getData(d), 0);
            const prevVal = this._prevRange
                .slice(i * this._resolution, (i + 1) * this._resolution)
                .reduce((total, d) => total + chart.getData(d), 0);
            curr.push(chart.total === "average" ? currVal / this._resolution : currVal);
            prev.push(chart.total === "average" ? prevVal / this._resolution : prevVal);
        }

        return { curr, prev };
    }

    private _getChartDefinition({ data: { curr, prev } }: Chart) {
        const rows = [
            ...curr.map((value, i) => ({ x: String(i), series: "Aktuell", value })),
            ...prev.map((value, i) => ({ x: String(i), series: "Vorperiode", value })),
        ];
        return lineChart(rows, {
            guides: false,
            tooltip: false,
            grouped: true,
            dashed: ["Vorperiode"],
            colors: { Aktuell: chartSeriesColors[0], Vorperiode: chartSeriesColors[7] },
            xPadding: 0,
        });
    }

    static styles = [
        shared,
        css`
            .venue-selector {
                font-size: var(--font-size-medium);
                padding-top: 0.3em;
                padding-bottom: 0.3em;
            }

            .charts {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(28em, 1fr));
            }

            .charts-tile {
                padding: 1em;
                border-right: dashed 1px var(--shade-2);
                border-bottom: dashed 1px var(--shade-2);
            }

            .chart-title {
                font-size: var(--font-size-big);
                font-weight: 600;
                padding-bottom: 0.5em;
            }

            .chart-total:last-child {
                text-align: right;
            }

            .chart-total-range {
                opacity: 0.5;
            }

            .chart-total-value {
                color: var(--color-highlight);
                font-size: var(--font-size-huge);
            }

            .chart-axis {
                padding: 0.5em;
                border-top: solid 1px var(--shade-2);
                color: var(--blue);
            }

            .highlight-data {
                transition: all 0.3s;
                margin: 0 0.5em;
                margin-bottom: -1em;
                width: 8.5em;
                flex: none;
                font-variant-numeric: tabular-nums;
            }

            .highlight-data.hide {
                opacity: 0;
            }

            .highlight-range {
                min-height: 1lh;
                white-space: nowrap;
                color: var(--color-fg);
                opacity: 0.5;
            }

            .highlight-data.current {
                text-align: right;
            }

            .highlight-value {
                font-size: var(--font-size-base);
                font-weight: 600;
            }

            .current .highlight-value {
                color: var(--color-primary);
            }

            .previous .highlight-value {
                color: var(--black);
            }
        `,
    ];

    render() {
        if (!app.company || !this.venue || !this._salesEntries || !this._timeReports || !this._costReports) {
            return html`
                <div
                    class="fullbleed center-aligning center-justifying vertical layout scrim"
                    ?hidden=${!this._loading}
                >
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            `;
        }

        const currRange = this._currRange;
        const currFromDate = formatDateShort(currRange[0]);
        const currToDate = formatDateShort(currRange[currRange.length - 1]);

        const prevRange = this._prevRange;
        const prevFromDate = formatDateShort(prevRange[0]);
        const prevToDate = formatDateShort(prevRange[prevRange.length - 1]);

        const showInd = this._showDataPoint;
        let showRangeCurr = "";
        let showRangePrev = "";

        if (showInd !== null) {
            let from = currRange[showInd * this._resolution];
            let to = currRange[(showInd + 1) * this._resolution - 1];
            showRangeCurr =
                from === to
                    ? `${formatWeekDayShort(from)}, ${formatDateShort(from)}`
                    : `${formatDateShort(from)} - ${formatDateShort(to)}`;

            from = prevRange[showInd * this._resolution];
            to = prevRange[(showInd + 1) * this._resolution - 1];
            showRangePrev =
                from === to
                    ? `${formatWeekDayShort(from)}, ${formatDateShort(from)}`
                    : `${formatDateShort(from)} - ${formatDateShort(to)}`;
        }

        return html`
            <div class="fullbleed vertical layout">
                <div class="header horizontal center-aligning layout padded-medium border-bottom">
                    <div class="horizontal tabs">
                        <button ?active=${this._range === 7 && this._resolution === 1} @click=${() => this.load(7, 1)}>
                            7 Tage
                        </button>
                        <button
                            ?active=${this._range === 14 && this._resolution === 1}
                            @click=${() => this.load(14, 1)}
                        >
                            14 Tage
                        </button>
                        <button
                            ?active=${this._range === 12 && this._resolution === 7}
                            @click=${() => this.load(12, 7)}
                        >
                            12 Wochen
                        </button>
                    </div>

                    <div class="stretch"></div>

                    <select
                        class="venue-selector"
                        @change=${this._selectVenue}
                        ?hidden=${app.accessibleVenues.length < 2}
                    >
                        ${app.accessibleVenues.map(
                            (venue) => html`
                                <option .value=${venue.id.toString()} ?selected=${venue.id === this.venueId}>
                                    ${venue.name}
                                </option>
                            `
                        )}
                    </select>
                </div>

                <ptc-scroller class="stretch">
                    <div class="charts">
                        ${repeat(
                            this._enabledCharts,
                            (chart) => chart.title,
                            (chart) => {
                                if (!chart.definition) {
                                    return;
                                }
                                let currTotal = chart.data.curr.reduce((total: number, val: number) => total + val, 0);
                                let prevTotal = chart.data.prev.reduce((total: number, val: number) => total + val, 0);

                                if (chart.total === "average") {
                                    currTotal /= chart.data.curr.length;
                                    prevTotal /= chart.data.prev.length;
                                }

                                const diff = prevTotal ? Math.round(((currTotal - prevTotal) / prevTotal) * 100) : 0;

                                return html`
                                    <div class="charts-tile">
                                        <div class="chart-title horizontal center-aligning layout">
                                            <div class="stretch">${chart.title}</div>
                                            <div>
                                                <ptc-ui-balance ?inverted=${Boolean(chart.inverted)} .value=${diff}>
                                                    <ptc-ui-icon name="icon.object.percentage"></ptc-ui-icon>
                                                </ptc-ui-balance>
                                            </div>
                                        </div>
                                        <div class="horizontal layout">
                                            <div class="chart-total blue">
                                                <div class="chart-total-range">${currFromDate} - ${currToDate}</div>
                                                <div class="chart-total-value">
                                                    ${chart.total === "average" ? "∅" : ""} ${formatNumber(currTotal)}
                                                    ${chart.unit}
                                                </div>
                                            </div>
                                            <div class="stretch"></div>
                                            <div class="chart-total black">
                                                <div class="chart-total-range">${prevFromDate} - ${prevToDate}</div>
                                                <div class="chart-total-value">
                                                    ${chart.total === "average" ? "∅" : ""} ${formatNumber(prevTotal)}
                                                    ${chart.unit}
                                                </div>
                                            </div>
                                        </div>

                                        <ptc-ui-chart
                                            label=${`${chart.title} im Vergleich zur Vorperiode`}
                                            .height=${200}
                                            .definition=${chart.definition}
                                            @chart-focus=${(e: CustomEvent<ChartPointEventDetail>) =>
                                                (this._showDataPoint = e.detail.point
                                                    ? Number(e.detail.point.xValue)
                                                    : null)}
                                            @mouseleave=${() => (this._showDataPoint = null)}
                                        ></ptc-ui-chart>

                                        <div class="chart-axis horizontal layout">
                                            <div>${currFromDate}</div>
                                            <div class="stretch"></div>
                                            <div
                                                class="highlight-data current ${showInd === null ? "hide" : ""}"
                                                role="status"
                                                aria-live="off"
                                            >
                                                <div class="highlight-range">${showRangeCurr}</div>
                                                <div class="highlight-value">
                                                    ${formatNumber(chart.data.curr[showInd || 0])}
                                                </div>
                                            </div>
                                            <div class="highlight-data previous ${showInd === null ? "hide" : ""}">
                                                <div class="highlight-range">${showRangePrev}</div>
                                                <div class="highlight-value">
                                                    ${formatNumber(chart.data.prev[showInd || 0])}
                                                </div>
                                            </div>
                                            <div class="stretch"></div>
                                            <div>${currToDate}</div>
                                        </div>
                                    </div>
                                `;
                            }
                        )}
                    </div>
                </ptc-scroller>

                <div
                    class="fullbleed center-aligning center-justifying vertical layout scrim"
                    ?hidden=${!this._loading}
                >
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            </div>
        `;
    }
}
